import React, { useState } from 'react';
import { Box, Stack, Typography, Card, CardContent } from '@mui/material';
import { styled } from '@mui/material/styles';
import { theme } from '../../constants/theme';
import { useThemeContext } from '../../hook/useThemeManager';
import { FONT_ENUM } from '../../types/global/fonts.types';
import CustomLazyLoadImage from '../Image/CustomLazyLoadImage';
import CustomButton from '../Button/CustomButton';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import MailIcon from '@mui/icons-material/Mail';
import FavoriteIcon from '@mui/icons-material/Favorite';
import CloseIcon from '@mui/icons-material/Close';
import { FilingStatusEnum } from '../../constants/types/api/application';
import { SpeedDial, SpeedDialAction, SpeedDialIcon } from '@mui/material';
import { CommentOutlined, ArchiveOutlined, TheaterComedyOutlined, MoreVert } from '@mui/icons-material';
import { ReusableDrawer } from '../ReusableDrawer/ReusableDrawer';
import { WorkProfile } from '../ScreenContainer/EsMyProfileContainer/WorkProfile';
import { IApplication } from '../../types/global/ListRender.types';
import { formatWithOrdinal } from '../../utils/AppUtils';
import { IWorkProfile } from '../../types/global/helper';
import { MessageDialogue } from '../MessageDialogue/MessageDialogue';

interface IApplicationCardProps {
    application: IApplication;
    onViewDetailsClick: (application: IApplication) => void;
    onRejectClick?: (application: IApplication) => void;
    onAcceptClick?: (application: IApplication) => void;
    onMessageClick?: (application: IApplication) => void;
    onArchiveClick?: (application: IApplication) => void;
    onCastClick?: (application: IApplication) => void;
}

const StyledCard = styled(Card)(({ theme }) => ({
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '12px',
    overflow: 'hidden',
    transition: 'transform 0.2s',
    '&:hover': {
        transform: 'translateY(-5px)',
    },
}));

export const ApplicationCard: React.FC<IApplicationCardProps> = ({
    application,
    onViewDetailsClick,
    onRejectClick,
    onAcceptClick,
    onMessageClick,
    onArchiveClick,
    onCastClick,
}) => {
    const { themeStyle } = useThemeContext();
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [speedDialOpen, setSpeedDialOpen] = useState(false);
    const [messageDialogueOpen, setMessageDialogueOpen] = useState(false);

    // Get status icon based on status
    const getStatusIcon = (status: string) => {
        switch (status) {
            case FilingStatusEnum.YES:
                return (
                    <CheckCircleIcon
                        style={{ color: theme[themeStyle].tertiary_green, fontSize: '16px', marginRight: '4px' }}
                    />
                );
            case FilingStatusEnum.NO:
                return (
                    <CancelIcon
                        style={{ color: theme[themeStyle].primary_red, fontSize: '16px', marginRight: '4px' }}
                    />
                );
            case FilingStatusEnum.INVITED:
                return (
                    <MailIcon
                        style={{ color: theme[themeStyle].primary_orange, fontSize: '16px', marginRight: '4px' }}
                    />
                );
            default:
                return null;
        }
    };

    // Update message click handler
    const handleMessageClick = () => {
        setMessageDialogueOpen(true);
        if (onMessageClick) onMessageClick(application);
    };

    // Speed dial actions
    const actions = [
        {
            icon: <CommentOutlined />,
            name: 'Message',
            onClick: () => {
                handleMessageClick();
                setSpeedDialOpen(false);
            },
        },
        {
            icon: <ArchiveOutlined />,
            name: 'Archive',
            onClick: () => {
                if (onArchiveClick) onArchiveClick(application);
                setSpeedDialOpen(false);
            },
        },
        {
            icon: <TheaterComedyOutlined />,
            name: 'Cast',
            onClick: () => {
                if (onCastClick) onCastClick(application);
                setSpeedDialOpen(false);
            },
        },
    ];

    // Handle view details click
    const handleViewDetailsClick = () => {
        setDrawerOpen(true);
        if (onViewDetailsClick) onViewDetailsClick(application);
    };

    return (
        <>
            <StyledCard sx={{ backgroundColor: theme[themeStyle].secondaryBg }}>
                <Stack
                    direction="column"
                    spacing={2}
                    alignItems="flex-start"
                    position="relative"
                    sx={{ borderBottom: `1px solid ${theme[themeStyle].grey}`, position: 'relative' }}>
                    <Box sx={{ position: 'relative', width: '100%' }}>
                        <CustomLazyLoadImage
                            src={application.profile.profile_picture}
                            alt="user profile"
                            style={{
                                width: '100%',
                                height: '250px',
                                objectFit: 'cover',
                            }}
                        />
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 0,
                                right: 0,
                                backgroundColor: theme[themeStyle].primaryGreen,
                                borderTopLeftRadius: 5,
                                borderBottomLeftRadius: 5,
                            }}>
                            <Typography
                                sx={{
                                    color: theme[themeStyle].true_white,
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    fontSize: '12px',
                                    px: 1,
                                }}>
                                {application.percentage_match}% Match
                            </Typography>
                        </Box>
                        <Box sx={{ position: 'absolute', bottom: 0, right: 10, zIndex: 2 }}>
                            <SpeedDial
                                ariaLabel="Applicant Actions"
                                icon={<SpeedDialIcon openIcon={<MoreVert />} />}
                                onClose={() => setSpeedDialOpen(false)}
                                onOpen={() => setSpeedDialOpen(true)}
                                open={speedDialOpen}
                                direction="left"
                                sx={{
                                    '& .MuiFab-primary': {
                                        width: 36,
                                        height: 36,
                                        minHeight: 36,
                                        backgroundColor: theme[themeStyle].tag_white,
                                        color: theme[themeStyle].deep_grey,
                                        '&:hover': {
                                            color: theme[themeStyle].deep_grey,
                                            backgroundColor: theme[themeStyle].tag_white,
                                        },
                                    },
                                }}>
                                {actions.map(action => (
                                    <SpeedDialAction
                                        key={action.name}
                                        icon={action.icon}
                                        tooltipTitle={action.name}
                                        onClick={action.onClick}
                                        sx={{
                                            backgroundColor: theme[themeStyle].tag_white,
                                            color: theme[themeStyle].deep_grey,
                                            '&:hover': {
                                                backgroundColor: '#eee',
                                                color: theme[themeStyle].deep_grey,
                                            },
                                        }}
                                    />
                                ))}
                            </SpeedDial>
                        </Box>
                    </Box>
                    <Box p={2}>
                        <Typography
                            sx={{
                                color: theme[themeStyle].tag_white,
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                fontSize: '16px',
                            }}>
                            {`${application.user?.firstName} ${application.user?.lastName}`}
                        </Typography>
                        <Typography
                            sx={{
                                color: theme[themeStyle].grey,
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                fontSize: '12px',
                                fontStyle: 'italic',
                                mt: 0.5,
                            }}>
                            Applied: {formatWithOrdinal(application.created_at)}
                        </Typography>
                    </Box>
                </Stack>
                <CardContent sx={{ flexGrow: 1, p: 2 }}>
                    <Stack spacing={2}>
                        <Box>
                            <Typography
                                sx={{
                                    color: theme[themeStyle].grey,
                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    fontSize: '12px',
                                }}>
                                Role
                            </Typography>
                            <Typography
                                sx={{
                                    color: theme[themeStyle].tag_white,
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    fontSize: '14px',
                                }}>
                                {application.role?.character || 'General'}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography
                                sx={{
                                    color: theme[themeStyle].grey,
                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    fontSize: '12px',
                                }}>
                                Status
                            </Typography>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    mt: 0.5,
                                }}>
                                {getStatusIcon(application.filing_status || 'unfiled')}
                                <Typography
                                    sx={{
                                        color: theme[themeStyle].tag_white,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        fontSize: '14px',
                                        textTransform: 'capitalize',
                                    }}>
                                    {application.filing_status || 'unfiled'}
                                </Typography>
                            </Box>
                        </Box>
                    </Stack>
                </CardContent>
                <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <CustomButton
                            label=""
                            withIcon={true}
                            beginningIcon={<CloseIcon />}
                            onClick={() => onRejectClick && onRejectClick(application)}
                            buttonProps={{ variant: 'outlined' }}
                            sx={{
                                borderColor: theme[themeStyle].primary_red,
                                color: theme[themeStyle].primary_red,
                                minWidth: '40px',
                                '&:hover': {
                                    borderColor: theme[themeStyle].primary_red,
                                    backgroundColor: 'rgba(244, 67, 54, 0.1)',
                                },
                            }}
                        />
                        <CustomButton
                            label=""
                            withIcon={true}
                            beginningIcon={<FavoriteIcon />}
                            onClick={() => onAcceptClick && onAcceptClick(application)}
                            buttonProps={{ variant: 'outlined' }}
                            sx={{
                                borderColor: theme[themeStyle].tertiary_green,
                                color: theme[themeStyle].tertiary_green,
                                minWidth: '40px',
                                '&:hover': {
                                    borderColor: theme[themeStyle].tertiary_green,
                                    backgroundColor: 'rgba(76, 175, 80, 0.1)',
                                },
                            }}
                        />
                        <CustomButton
                            label=""
                            withIcon={true}
                            beginningIcon={<CommentOutlined />}
                            onClick={handleMessageClick}
                            buttonProps={{ variant: 'outlined' }}
                            sx={{
                                borderColor: theme[themeStyle].primary,
                                color: theme[themeStyle].primary,
                                minWidth: '40px',
                                '&:hover': {
                                    borderColor: theme[themeStyle].primary,
                                    backgroundColor: 'rgba(33, 150, 243, 0.1)',
                                },
                            }}
                        />
                    </Box>
                    <CustomButton
                        label="View Details"
                        onClick={handleViewDetailsClick}
                        customStyle={{
                            borderRadius: '5px',
                        }}
                    />
                </Box>
            </StyledCard>

            {/* Drawer for profile details */}
            <ReusableDrawer
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                content={
                    <WorkProfile
                        workProfile={[application.profile as unknown as IWorkProfile]}
                        external={true}
                        isPreviewMode
                        applicationData={application}
                        onAcceptApplication={() => onAcceptClick && onAcceptClick(application)}
                        onRejectApplication={() => onRejectClick && onRejectClick(application)}
                        onMessageApplication={handleMessageClick}
                    />
                }
                anchor={'right'}
            />

            {/* Message Dialogue */}
            <MessageDialogue
                open={messageDialogueOpen}
                onClose={() => setMessageDialogueOpen(false)}
                recipient={application.user}
                roleId={application.role._id}
                auditionId={application.audition}
            />
        </>
    );
};
